// Fix this as soon as possible before any vine code is initialized it’s needed in order for tanstack form to be able to correctly retrieve the standard schema issue
import { patchVineStandardSchema } from './utils/patch-vine-standard-schema'
patchVineStandardSchema()

import { keys } from '@gozoki/api'
import * as Sentry from '@sentry/react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import type { PermissionRequirement } from '~/utils/rbac'
import {
  getRbacMissingPermissions,
  toastMutationError,
  toastRbacForbidden,
} from '~/utils/rbacQueryErrors'
import ErrorComponent from './components/communication/ErrorComponent'
import { routeTree } from './routeTree.gen'

import './assets/styles/index.css'

// Rend les validateurs vine directement compatibles TanStack Form (corrige `~standard.path`).

// Sentry config
Sentry.init({
  dsn: 'https://66aef9475a1149f48d9750a7ef6b234a@o4505583710699520.ingest.sentry.io/4505583821324288',
  enabled: process.env.NODE_ENV === 'production',
  tracesSampleRate: 0,
})

// Message d'erreur générique d'une mutation, toasté par le MutationCache. Opt-in : les mutations
// qui gèrent déjà leur `onError` (la majorité, historiquement) ne déclarent pas de `meta` et ne
// sont donc pas toastées deux fois.
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { errorMessage?: string }
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {},
    queries: {
      // Un refus RBAC est déterministe : réessayer ne peut que répéter le 403.
      retry: (failureCount, error) => getRbacMissingPermissions(error) === null && failureCount < 3,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const fallback = mutation.meta?.errorMessage
      if (fallback) toastMutationError(error, fallback)
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      const missingPermissions = getRbacMissingPermissions(error)
      if (missingPermissions) toastRbacForbidden(missingPermissions)
    },
  }),
})

// Fraîcheur ciblée sur la section RH uniquement (clés préfixées `['hr']`). Sans staleTime, react-query
// refetch à chaque mount ET chaque focus fenêtre ; combiné au routing (un onglet = une route → remount
// à chaque switch), ça multipliait les requêtes. 60s = switch d'onglet / re-focus dans la minute servi
// du cache. Scopé RH via setQueryDefaults pour ne PAS changer le comportement du reste du BO (vues
// temps-réel). Les mutations invalident leurs queries → modifs utilisateur instantanées malgré le
// staleTime. Surchargé par query là où voulu (dashboard/collab-list 5min, workshifts 2min).
queryClient.setQueryDefaults(keys.hr.all, { staleTime: 1000 * 60 })

// Create the router instance
const router = createRouter({
  context: {
    queryClient,
  },
  defaultErrorComponent: ErrorComponent,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultStructuralSharing: true,
  notFoundMode: 'root',
  routeTree,
  scrollRestoration: true,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }

  interface StaticDataRouteOption {
    /**
     * Droit(s) requis pour afficher la page : un tuple, un tableau de tuples (ET), ou
     * `{ any: [...] }` (OU) pour une page accessible dès qu'un de ses sous-onglets l'est.
     */
    permission?: PermissionRequirement
  }
}

// Render the app
const rootElement = document.getElementById('root')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}
